import { useQuery } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";

import type { AddressDto, HubSpotProjectConfigurationDto, UserDto } from "@/api/types";
import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Form } from "@/components/Form/Form";
import { FormCheckbox } from "@/components/Form/FormCheckbox";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { createRequiredStringRule } from "@/helpers/rules";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { useAddressQueries } from "@/queries/addresses/queryOptions";
import { integrationMutations } from "@/queries/integrations/mutations";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

export type HubSpotFormValues = {
  enabled: boolean;
  privateAppAccessToken: string;
  syncCompaniesFromHubSpot: boolean;
  syncContactsFromHubSpot: boolean;
  syncContactsToHubSpot: boolean;
  registerContactMomentsToHubSpot: boolean;
  requesterUser: UserDto | undefined;
  fallbackAddress: AddressDto | undefined;
};

export function HubSpotPage(): React.ReactNode {
  const { t } = useTranslation();
  const slug = useSlug();
  const hasPermission = usePermission();
  const integrationQueries = useIntegrationQueries();
  const userQueries = useUserQueries();
  const addressQueries = useAddressQueries();

  const {
    data: config,
    isFetching: isLoadingConfig,
    error: configError,
  } = useQuery(integrationQueries.hubSpotConfiguration());
  const {
    data: adminUsers,
    isFetching: isLoadingAdminUsers,
    error: adminUsersError,
  } = useQuery(userQueries.listAdmins());
  const {
    data: addresses,
    isFetching: isLoadingAddresses,
    error: addressesError,
  } = useQuery(addressQueries.list({ limit: 1000 }));
  const mutation = integrationMutations.useEditHubSpotConfiguration();

  if (!hasPermission((x) => x.integrations.canManageHubSpot)) {
    return <ErrorPage status={404} />;
  }

  if (configError || adminUsersError || addressesError) {
    return <ErrorPage error={configError || adminUsersError || addressesError} />;
  }

  if (isLoadingConfig || isLoadingAdminUsers || isLoadingAddresses) {
    return <FullSizeLoader withPadding />;
  }

  const name = t("page.integration.hubspot.title");

  return (
    <DocumentPage
      containerBoundary="inherit"
      contentSize="constrained"
      tabTitle={name}
      breadcrumbs={
        <Breadcrumbs
          pages={[{ name: t("page.integration.title"), to: routes.projects.integrations({ slug }) }, { name }]}
        />
      }
    >
      <HubSpotForm
        defaultValues={{
          enabled: config?.enabled || false,
          privateAppAccessToken: config?.privateAppAccessToken || "",
          syncCompaniesFromHubSpot: config?.syncCompaniesFromHubSpot || false,
          syncContactsFromHubSpot: config?.syncContactsFromHubSpot || false,
          syncContactsToHubSpot: config?.syncContactsToHubSpot || false,
          registerContactMomentsToHubSpot: config?.registerContactMomentsToHubSpot || false,
          requesterUser:
            !config?.requesterUserId || !adminUsers
              ? undefined
              : adminUsers.find((u) => u.id === config.requesterUserId),
          fallbackAddress:
            !config?.fallbackAddressId || !addresses
              ? undefined
              : addresses.items.find((a) => a.id === config.fallbackAddressId),
        }}
        adminUsers={adminUsers || []}
        addresses={addresses?.items || []}
        isSubmitting={mutation.isPending}
        onSubmit={mutation.mutate}
      />
    </DocumentPage>
  );
}

interface HubSpotFormProps {
  defaultValues: Partial<HubSpotFormValues>;
  adminUsers: UserDto[];
  addresses: AddressDto[];
  isSubmitting: boolean;
  onSubmit: (data: HubSpotProjectConfigurationDto) => void;
}

function HubSpotForm({
  defaultValues,
  adminUsers,
  addresses,
  isSubmitting,
  onSubmit,
}: HubSpotFormProps): React.ReactNode {
  const { t } = useTranslation();
  const formMethods = useForm<HubSpotFormValues>({ defaultValues });
  const currEnabled = useWatch({ control: formMethods.control, name: "enabled" });

  function handleSubmit(formValues: HubSpotFormValues) {
    onSubmit({
      enabled: formValues.enabled,
      privateAppAccessToken: formValues.privateAppAccessToken,
      syncCompaniesFromHubSpot: formValues.syncCompaniesFromHubSpot,
      syncContactsFromHubSpot: formValues.syncContactsFromHubSpot,
      syncContactsToHubSpot: formValues.syncContactsToHubSpot,
      registerContactMomentsToHubSpot: formValues.registerContactMomentsToHubSpot,
      requesterUserId: formValues.requesterUser?.id,
      fallbackAddressId: formValues.fallbackAddress?.id,
    });
  }

  return (
    <Form.Root formMethods={formMethods} onSubmit={handleSubmit}>
      <Form.Container title={t("page.integration.hubspot.title")}>
        <Form.Body>
          <FormCheckbox<HubSpotFormValues, "enabled">
            id="enabled"
            name="enabled"
            label={t("common.action.enable")}
            onChange={(e) => {
              if (currEnabled && !e.target.checked) {
                formMethods.clearErrors();
              }
            }}
          />

          <FormField label={t("page.integration.hubspot.form.private-app-access-token")} required>
            <FormInput<HubSpotFormValues, "privateAppAccessToken">
              id="privateAppAccessToken"
              name="privateAppAccessToken"
              type="password"
              rules={{
                validate: {
                  required: currEnabled
                    ? createRequiredStringRule(t, "page.integration.hubspot.form.private-app-access-token")
                    : () => undefined,
                },
              }}
              disabled={!currEnabled}
            />
          </FormField>

          <FormCheckbox<HubSpotFormValues, "syncCompaniesFromHubSpot">
            id="syncCompaniesFromHubSpot"
            name="syncCompaniesFromHubSpot"
            label={t("page.integration.hubspot.form.sync-companies-from-hubspot")}
            disabled={!currEnabled}
          />

          <FormCheckbox<HubSpotFormValues, "syncContactsFromHubSpot">
            id="syncContactsFromHubSpot"
            name="syncContactsFromHubSpot"
            label={t("page.integration.hubspot.form.sync-contacts-from-hubspot")}
            disabled={!currEnabled}
          />

          <FormCheckbox<HubSpotFormValues, "syncContactsToHubSpot">
            id="syncContactsToHubSpot"
            name="syncContactsToHubSpot"
            label={t("page.integration.hubspot.form.sync-contacts-to-hubspot")}
            disabled={!currEnabled}
          />

          <FormCheckbox<HubSpotFormValues, "registerContactMomentsToHubSpot">
            id="registerContactMomentsToHubSpot"
            name="registerContactMomentsToHubSpot"
            label={t("page.integration.hubspot.form.register-contact-moments-to-hubspot")}
            disabled={!currEnabled}
          />

          <FormField label={t("page.integration.hubspot.form.requester-user")}>
            <div className="max-w-search-wide">
              <FormSelect<HubSpotFormValues, UserDto>
                items={adminUsers || []}
                keySelector={(user) => user.id}
                renderOption={(user) => `${user.firstName} ${user.lastName}`}
                name="requesterUser"
                disabled={isSubmitting || !currEnabled}
              />
            </div>
          </FormField>

          <FormField label={t("page.integration.hubspot.form.fallback-address")}>
            <div className="max-w-search-wide">
              <FormSelect<HubSpotFormValues, AddressDto>
                items={addresses || []}
                keySelector={(address) => address.id}
                renderOption={(address) => address.location || address.id}
                name="fallbackAddress"
                disabled={isSubmitting || !currEnabled}
              />
            </div>
          </FormField>
        </Form.Body>
        <Form.Footer
          renderPrimaryAction={() => (
            <Button type="submit" isLoading={isSubmitting}>
              {t("common.action.save")}
            </Button>
          )}
        />
      </Form.Container>
    </Form.Root>
  );
}
